import styled from '@emotion/styled';
import { useEffect, useRef } from 'react';
import { BrandColors } from 'storybookConfig/stories/theme';

/**
 * The website's plasma field (carro-website `src/components/ShaderBackground.tsx`): a slow,
 * grained WebGL gradient in the brand colours. It fills its parent, which should be
 * `position: relative` and carry a still wash (`wash('inkwell')`) as the fallback — that is
 * what shows before the first frame, without WebGL, or after a lost context.
 *
 * Reduced motion and low-end devices get one still frame instead of the drift. Changing `palette`
 * blends the field into the new colours over `PALETTE_BLEND_MS` rather than cutting.
 */

export type ShaderPalette = 'inkwell' | 'dusk' | 'midnight' | 'ember' | 'sweep' | 'iris';

/* The dark grounds start on inkwell twice so most of the field stays dark behind white type. */
const PALETTES: Record<ShaderPalette, string[]> = {
  inkwell: [
    BrandColors.inkwell,
    BrandColors.irisWash,
    BrandColors.signalBlue,
    BrandColors.lilacMist,
  ],
  dusk: [BrandColors.inkwell, BrandColors.inkwell, BrandColors.irisWash, BrandColors.orchid],
  midnight: [
    BrandColors.inkwell,
    BrandColors.inkwell,
    BrandColors.signalBlue,
    BrandColors.irisWash,
  ],
  ember: [BrandColors.inkwell, BrandColors.inkwell, BrandColors.irisWash, BrandColors.coralGlow],
  sweep: [BrandColors.inkwell, BrandColors.signalBlue, BrandColors.orchid, BrandColors.canvasWhite],
  iris: [BrandColors.inkwell, BrandColors.irisWash, BrandColors.orchid, BrandColors.canvasWhite],
};

const BRIGHTNESS: Partial<Record<ShaderPalette, number>> = {
  dusk: -0.34,
  midnight: -0.3,
  ember: -0.3,
};

/** How long a palette change takes to blend in. */
export const PALETTE_BLEND_MS = 840;

const UNIFORMS = {
  colorCount: 4,
  scale: 1.5,
  intensity: 0.48,
  warp: 0.08,
  detail: 2.4,
  contrast: 0.98,
  brightness: -0.14,
  saturation: 1.08,
  vignette: 0.5,
  blur: 0.016,
  grain: 0.12,
  drift: 0.156,
  timeScale: 0.72,
};

const VERT = `attribute vec2 a_position;
void main() { gl_Position = vec4(a_position, 0.0, 1.0); }`;

// Unchanged from the website, minus the uniforms it never varies (rotation, offset).
const FRAG = `#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
uniform vec3 u_colors[8];
uniform vec4 u_scene;
uniform vec4 u_shape;
uniform vec4 u_surface;
uniform vec4 u_finish;
uniform vec2 u_motion;
#define u_resolution u_scene.xy
#define u_time u_scene.z
#define u_colorCount u_scene.w
#define u_scale u_shape.x
#define u_intensity u_shape.y
#define u_warp u_shape.w
#define u_detail u_surface.x
#define u_contrast u_surface.y
#define u_brightness u_surface.z
#define u_saturation u_surface.w
#define u_vignette u_finish.y
#define u_blur u_finish.z
#define u_grain u_finish.w
#ifdef GL_FRAGMENT_PRECISION_HIGH
#define u_seed u_motion.x
#else
#define u_seed mod(u_motion.x, 31.0)
#endif
#define u_drift u_motion.y

float hash21(vec2 p) {
#ifndef GL_FRAGMENT_PRECISION_HIGH
  p = mod(p, 31.0);
#endif
  p = fract(p * vec2(234.34, 435.345));
  p += dot(p, p + 34.23);
  return fract(p.x * p.y);
}
float grainHash(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash21(i), hash21(i + vec2(1.0, 0.0)), u.x),
    mix(hash21(i + vec2(0.0, 1.0)), hash21(i + vec2(1.0, 1.0)), u.x), u.y);
}
float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 5; i++) { v += a * noise(p); p = p * 2.03 + vec2(17.0, 9.2); a *= 0.5; }
  return v;
}
vec3 srgbToLinear(vec3 c) { return mix(c / 12.92, pow((c + 0.055) / 1.055, vec3(2.4)), step(0.04045, c)); }
vec3 linearToSrgb(vec3 c) {
  return mix(c * 12.92, 1.055 * pow(max(c, vec3(0.0)), vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c));
}
vec3 linToOklab(vec3 c) {
  float l = pow(max(0.4122214708 * c.r + 0.5363325363 * c.g + 0.0514459929 * c.b, 0.0), 1.0 / 3.0);
  float m = pow(max(0.2119034982 * c.r + 0.6806995451 * c.g + 0.1073969566 * c.b, 0.0), 1.0 / 3.0);
  float s = pow(max(0.0883024619 * c.r + 0.2817188376 * c.g + 0.6299787005 * c.b, 0.0), 1.0 / 3.0);
  return vec3(0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717660 * m - 0.8086757660 * s);
}
vec3 oklabToLin(vec3 c) {
  float l = c.x + 0.3963377774 * c.y + 0.2158037573 * c.z;
  float m = c.x - 0.1055613458 * c.y - 0.0638541728 * c.z;
  float s = c.x - 0.0894841775 * c.y - 1.2914855480 * c.z;
  l = l * l * l; m = m * m * m; s = s * s * s;
  return vec3(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s);
}
vec3 mixColour(vec3 a, vec3 b, float t) {
  vec3 la = linToOklab(srgbToLinear(a));
  vec3 lb = linToOklab(srgbToLinear(b));
  return clamp(linearToSrgb(oklabToLin(mix(la, lb, t))), 0.0, 1.0);
}
vec3 palette(float x) {
  float n = max(u_colorCount - 1.0, 1.0);
  float f = clamp(x, 0.0, 1.0) * n;
  vec3 col = u_colors[0];
  for (int i = 0; i < 7; i++) {
    if (float(i) < n) col = mixColour(col, u_colors[i + 1], smoothstep(0.0, 1.0, clamp(f - float(i), 0.0, 1.0)));
  }
  return col;
}
vec3 shade(vec2 p, float t) {
  float k = 2.0 + u_intensity * 6.0;
  float v = sin(p.x * k + t) + sin(p.y * k * 0.8 - t * 0.7)
    + sin((p.x + p.y) * k * 0.6 + t * 0.5) + sin(length(p) * k * 1.2 - t);
  return palette(0.5 + 0.5 * sin(v + u_seed));
}
void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution.xy;
  vec2 p = (gl_FragCoord.xy - 0.5 * u_resolution.xy) / min(u_resolution.x, u_resolution.y);
  p *= u_scale;
  p += u_drift * vec2(sin(u_time * 0.31), cos(u_time * 0.23));
  p += u_warp * (vec2(fbm(p * u_detail + u_seed), fbm(p * u_detail + vec2(5.2, 1.3))) - 0.5);
  float pe = u_blur * u_scale;
  vec3 col = shade(p, u_time) * 0.36
    + shade(p + vec2(pe, 0.0), u_time) * 0.16 + shade(p - vec2(pe, 0.0), u_time) * 0.16
    + shade(p + vec2(0.0, pe), u_time) * 0.16 + shade(p - vec2(0.0, pe), u_time) * 0.16;
  col = (col - 0.5) * u_contrast + 0.5;
  col = mix(vec3(dot(col, vec3(0.299, 0.587, 0.114))), col, u_saturation);
  col += u_brightness;
  col *= 1.0 - u_vignette * smoothstep(0.35, 1.0, length(uv - 0.5) * 1.41421356);
  col += (grainHash(gl_FragCoord.xy + vec2(u_seed * 17.0, u_seed * 31.0)) - 0.5) * u_grain;
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
}`;

type Quality = 'still' | 'balanced' | 'high';

type PerformanceNavigator = Navigator & {
  deviceMemory?: number;
  connection?: { effectiveType?: string; saveData?: boolean };
};

/* The same tiers as the website: a weak device draws one frame, a mid one draws at 30fps. */
const chooseQuality = (): Quality => {
  const {
    deviceMemory: memory,
    connection,
    hardwareConcurrency: cores,
  } = navigator as PerformanceNavigator;
  if (
    connection?.saveData ||
    connection?.effectiveType === 'slow-2g' ||
    connection?.effectiveType === '2g' ||
    (memory !== undefined && memory <= 2) ||
    (cores !== undefined && cores <= 2)
  ) {
    return 'still';
  }
  if (
    window.matchMedia('(pointer: coarse)').matches ||
    (memory !== undefined && memory <= 4) ||
    (cores !== undefined && cores <= 4)
  ) {
    return 'balanced';
  }
  return 'high';
};

const PIXEL_BUDGET = { high: 1_200_000, balanced: 500_000 };

const hexToRgb = (hex: string) =>
  [1, 3, 5].map((i) => Number.parseInt(hex.slice(i, i + 2), 16) / 255);

/*
 * The canvas (or, for a still field, the frame it drew) fills the host. Each effect run makes its
 * own canvas: a context given up with `loseContext()` cannot be taken again from the same canvas,
 * which StrictMode's remount and a prop change would otherwise try.
 */
const Host = styled.div`
  position: absolute;
  inset: 0;

  & > canvas,
  & > img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }
`;

interface ShaderBackgroundProps {
  palette?: ShaderPalette;
  /** The field's seed; a different seed draws a different field from the same palette. */
  seed?: number;
  /** Draw one frame and keep it as an image, giving the WebGL context back. */
  still?: boolean;
  /** How much of the field shows: below 1 zooms in (fewer, larger shapes). */
  zoom?: number;
}

const ShaderBackground = ({
  palette = 'midnight',
  seed = 7,
  still = false,
  zoom = 1,
}: ShaderBackgroundProps) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const paletteRef = useRef(palette);
  const redrawRef = useRef<() => void>();
  paletteRef.current = palette;
  useEffect(() => redrawRef.current?.(), [palette]);

  // A still field is drawn once, so a new palette draws it again instead of blending.
  const stillKey = still ? palette : '';

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    const canvas = document.createElement('canvas');
    host.appendChild(canvas);
    const gl = canvas.getContext('webgl', {
      alpha: false,
      antialias: false,
      powerPreference: 'low-power',
    });
    // Browsers cap live WebGL contexts, so give each one back as soon as it is done.
    const release = () => {
      gl?.getExtension('WEBGL_lose_context')?.loseContext();
      canvas.remove();
    };
    if (!gl) {
      release();
      return undefined;
    }

    const compile = (type: number, source: string) => {
      const shader = gl.createShader(type);
      if (!shader) return null;
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (gl.getShaderParameter(shader, gl.COMPILE_STATUS)) return shader;
      gl.deleteShader(shader);
      return null;
    };

    const vertex = compile(gl.VERTEX_SHADER, VERT);
    const fragment = compile(gl.FRAGMENT_SHADER, FRAG);
    const program = gl.createProgram();
    if (!vertex || !fragment || !program) {
      // Whichever did compile would otherwise be left behind.
      if (vertex) gl.deleteShader(vertex);
      if (fragment) gl.deleteShader(fragment);
      if (program) gl.deleteProgram(program);
      release();
      return undefined;
    }
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    gl.deleteShader(vertex);
    gl.deleteShader(fragment);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      gl.deleteProgram(program);
      release();
      return undefined;
    }
    gl.useProgram(program);

    // One triangle that covers the viewport.
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const position = gl.getAttribLocation(program, 'a_position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    const at = (name: string) => gl.getUniformLocation(program, name);
    gl.uniform4f(at('u_shape'), UNIFORMS.scale * zoom, UNIFORMS.intensity, 0.5, UNIFORMS.warp);
    gl.uniform4f(at('u_finish'), 0, UNIFORMS.vignette, UNIFORMS.blur, UNIFORMS.grain);
    gl.uniform2f(at('u_motion'), seed, UNIFORMS.drift);
    const scene = at('u_scene');
    const colorsAt = at('u_colors');
    const surfaceAt = at('u_surface');

    // The palette as eight colours and a brightness, so two palettes blend number by number.
    const look = (name: ShaderPalette) => {
      const rgb = PALETTES[name].map(hexToRgb);
      while (rgb.length < 8) rgb.push(rgb[rgb.length - 1]);
      return [...rgb.flat(), BRIGHTNESS[name] ?? UNIFORMS.brightness];
    };
    let shown = look(paletteRef.current);
    let from = shown;
    let to = shown;
    let target = paletteRef.current;
    let blendStart = 0;

    const quality = chooseQuality();
    const budget = PIXEL_BUDGET[quality === 'high' ? 'high' : 'balanced'];
    const frameInterval = quality === 'high' ? 0 : 1000 / 30;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const animating = () => !still && !reduced.matches && quality !== 'still';

    const start = performance.now();
    let frame = 0;
    let last = 0;
    let visible = document.visibilityState === 'visible';
    let disposed = false;
    let frameImage: HTMLImageElement | null = null;

    const resize = () => {
      const { width, height } = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rawWidth = Math.max(1, Math.round(width * dpr));
      const rawHeight = Math.max(1, Math.round(height * dpr));
      // The field is a soft gradient, so it survives being drawn small and scaled up.
      const scale = Math.min(1, Math.sqrt(budget / (rawWidth * rawHeight)));
      const w = Math.max(1, Math.round(rawWidth * scale));
      const h = Math.max(1, Math.round(rawHeight * scale));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        gl.viewport(0, 0, w, h);
      }
    };

    const requestRender = () => {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      if (!disposed && visible && frame === 0) frame = requestAnimationFrame(render);
    };

    function render(now: number) {
      frame = 0;
      if (disposed || !visible) return;
      if (frameInterval && last && now - last < frameInterval - 1) {
        requestRender();
        return;
      }
      last = now;
      resize();
      const time = animating() ? ((now - start) / 1000) * UNIFORMS.timeScale : 0;
      if (paletteRef.current !== target) {
        target = paletteRef.current;
        from = shown;
        to = look(paletteRef.current);
        blendStart = now;
      }
      const blend = animating() ? Math.min(1, (now - blendStart) / PALETTE_BLEND_MS) : 1;
      const eased = blend * blend * (3 - 2 * blend);
      shown = from.map((v, i) => v + (to[i] - v) * eased);
      gl!.uniform3fv(colorsAt, new Float32Array(shown.slice(0, 24)));
      gl!.uniform4f(surfaceAt, UNIFORMS.detail, UNIFORMS.contrast, shown[24], UNIFORMS.saturation);
      gl!.uniform4f(scene, canvas!.width, canvas!.height, time, UNIFORMS.colorCount);
      gl!.drawArrays(gl!.TRIANGLES, 0, 3);
      if (still) {
        // Read the frame back in the same task, before the buffer is cleared, and let it stand in.
        frameImage = document.createElement('img');
        frameImage.alt = '';
        frameImage.src = canvas.toDataURL();
        host!.appendChild(frameImage);
        disposed = true;
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        stop();
        return;
      }
      if (animating() || blend < 1) requestRender();
    }

    const observer = new ResizeObserver(requestRender);
    observer.observe(canvas);
    const onVisibility = () => {
      visible = document.visibilityState === 'visible';
      requestRender();
    };
    document.addEventListener('visibilitychange', onVisibility);
    reduced.addEventListener('change', requestRender);
    redrawRef.current = requestRender;

    let stopped = false;
    function stop() {
      if (stopped) return;
      stopped = true;
      cancelAnimationFrame(frame);
      observer.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      reduced.removeEventListener('change', requestRender);
      gl!.deleteBuffer(buffer);
      gl!.deleteProgram(program);
      release();
    }

    requestRender();

    return () => {
      disposed = true;
      stop();
      frameImage?.remove();
    };
  }, [seed, still, zoom, stillKey]);

  return <Host ref={hostRef} aria-hidden="true" />;
};

export default ShaderBackground;
